/**
 * PropertySections Component
 *
 * Renders collapsible sections for property details:
 * - Overview (description)
 * - Price History
 * - Features
 * - Property Details
 */
import { openQpublicPopup } from '../qpublicLink.js';
export class PropertySections {
    options;
    sectionState;
    constructor(options) {
        this.options = options;
        this.sectionState = {
            overview: true,
            priceHistory: false,
            features: false,
            propertyDetails: false,
            ...options.initialSectionState,
        };
    }
    render() {
        const { property, listing } = this.options;
        let html = '';
        // Overview Section (Description)
        const description = listing?.publicRemarks || property.publicRemarks;
        if (description) {
            // Render description as HTML (MLS data often contains HTML formatting)
            html += this.renderSection('overview', 'Overview', `
        <div class="sm-description">
          ${this.sanitizeHtml(description)}
        </div>
      `);
        }
        // Price History Section
        html += this.renderPriceHistorySection();
        // Features Section
        const featuresHtml = this.renderFeaturesContent();
        if (featuresHtml) {
            html += this.renderSection('features', 'Features', featuresHtml);
        }
        // Property Details Section
        html += this.renderPropertyDetailsSection();
        this.options.container.innerHTML = html;
        this.attachEventListeners();
    }
    renderSection(id, title, content) {
        const isCollapsed = !this.sectionState[id];
        return `
      <div class="sm-property-section${isCollapsed ? ' collapsed' : ''}" data-section="${id}">
        <button class="sm-section-header">
          <span>${title}</span>
          <span class="sm-section-chevron">&#9660;</span>
        </button>
        <div class="sm-section-content">
          ${content}
        </div>
      </div>
    `;
    }
    renderPriceHistorySection() {
        const { property, listing } = this.options;
        const items = [];
        // Current/List Price
        const currentPrice = property.listPrice || listing?.listPrice;
        const originalPrice = property.originalListPrice || listing?.originalListPrice;
        if (currentPrice) {
            const label = property.status === 'sold' ? 'Sold Price' : 'List Price';
            let item = `
        <div class="sm-price-history-item">
          <span class="sm-price-history-label">${label}</span>
          <span class="sm-price-history-value">${this.formatPrice(currentPrice)}</span>
        </div>
      `;
            items.push(item);
        }
        // Price change
        if (originalPrice && currentPrice && originalPrice !== currentPrice) {
            const change = currentPrice - originalPrice;
            const changePercent = ((change / originalPrice) * 100).toFixed(1);
            const isIncrease = change > 0;
            items.push(`
        <div class="sm-price-history-item">
          <span class="sm-price-history-label">
            Original Price
            <span class="sm-price-history-change ${isIncrease ? 'increase' : 'drop'}">
              (${isIncrease ? '+' : ''}${changePercent}%)
            </span>
          </span>
          <span class="sm-price-history-value">${this.formatPrice(originalPrice)}</span>
        </div>
      `);
        }
        // Close price for sold properties
        if (property.status === 'sold' && property.closePrice && property.closeDate) {
            items.push(`
        <div class="sm-price-history-item">
          <span class="sm-price-history-label">
            Closed
            <span class="sm-price-history-date">${this.formatDate(property.closeDate)}</span>
          </span>
          <span class="sm-price-history-value">${this.formatPrice(property.closePrice)}</span>
        </div>
      `);
        }
        // Sales history for off-market properties
        if (property.sales && property.sales.length > 0) {
            for (const sale of property.sales.slice(0, 5)) {
                items.push(`
          <div class="sm-price-history-item">
            <span class="sm-price-history-label">
              Sale
              <span class="sm-price-history-date">${this.formatDate(sale.saleDate)}</span>
            </span>
            <span class="sm-price-history-value">${this.formatPrice(sale.salePrice)}</span>
          </div>
        `);
            }
        }
        if (items.length === 0) {
            return '';
        }
        return this.renderSection('priceHistory', 'Price History', `
      <div class="sm-price-history-list">
        ${items.join('')}
      </div>
    `);
    }
    renderFeaturesContent() {
        const { listing } = this.options;
        if (!listing)
            return '';
        const categories = [];
        // Views
        if (listing.view && listing.view.length > 0) {
            categories.push({ title: 'Views', items: listing.view });
        }
        // Interior Features
        if (listing.interiorFeatures && listing.interiorFeatures.length > 0) {
            categories.push({ title: 'Interior', items: listing.interiorFeatures });
        }
        // Exterior Features
        if (listing.exteriorFeatures && listing.exteriorFeatures.length > 0) {
            categories.push({ title: 'Exterior', items: listing.exteriorFeatures });
        }
        // Pool
        if (listing.poolFeatures && listing.poolFeatures.length > 0) {
            categories.push({ title: 'Pool', items: listing.poolFeatures });
        }
        // Appliances
        if (listing.appliances && listing.appliances.length > 0) {
            categories.push({ title: 'Appliances', items: listing.appliances });
        }
        // Community
        if (listing.communityFeatures && listing.communityFeatures.length > 0) {
            categories.push({ title: 'Community', items: listing.communityFeatures });
        }
        // Security
        if (listing.securityFeatures && listing.securityFeatures.length > 0) {
            categories.push({ title: 'Security', items: listing.securityFeatures });
        }
        // Parking
        if (listing.parkingFeatures && listing.parkingFeatures.length > 0) {
            categories.push({ title: 'Parking', items: listing.parkingFeatures });
        }
        if (categories.length === 0)
            return '';
        return `
      <div class="sm-features-grid">
        ${categories.map(cat => `
          <div class="sm-features-category">
            <div class="sm-features-category-title">${cat.title}</div>
            <div class="sm-features-list">
              ${cat.items.map(item => `<span class="sm-feature-item">${this.escapeHtml(item)}</span>`).join('')}
            </div>
          </div>
        `).join('')}
      </div>
    `;
    }
    renderPropertyDetailsSection() {
        const { property, listing } = this.options;
        const rows = [];
        // Property Type
        const propertyType = listing?.propertyType;
        if (propertyType) {
            rows.push(this.detailRow('Property Type', propertyType));
        }
        // Year Built
        const yearBuilt = property.yearBuilt || listing?.yearBuilt;
        if (yearBuilt) {
            rows.push(this.detailRow('Year Built', String(yearBuilt)));
        }
        // Living Area
        const sqft = property.sqft || listing?.livingArea;
        if (sqft) {
            rows.push(this.detailRow('Living Area', `${sqft.toLocaleString()} sq ft`));
        }
        // Lot Size
        if (listing?.lotSizeSqft) {
            const lotDisplay = listing.lotSizeSqft >= 43560
                ? `${(listing.lotSizeSqft / 43560).toFixed(2)} acres`
                : `${listing.lotSizeSqft.toLocaleString()} sq ft`;
            rows.push(this.detailRow('Lot Size', lotDisplay));
        }
        // Stories
        if (listing?.stories) {
            rows.push(this.detailRow('Stories', String(listing.stories)));
        }
        // Zoning
        if (listing?.zoning) {
            rows.push(this.detailRow('Zoning', listing.zoning));
        }
        // HOA Fee
        if (listing?.associationFee) {
            const frequency = listing.associationFeeFrequency || 'Monthly';
            rows.push(this.detailRow('HOA Fee', `${this.formatPrice(listing.associationFee)}/${frequency.toLowerCase()}`));
        }
        // Taxes
        if (listing?.taxAnnualAmount) {
            const taxYear = listing.taxYear ? ` (${listing.taxYear})` : '';
            rows.push(this.detailRow('Annual Taxes', `${this.formatPrice(listing.taxAnnualAmount)}${taxYear}`));
        }
        // TMK - clickable, opens the county's qPublic tax-record lookup in a
        // popup (qPublic blocks iframe embedding via X-Frame-Options, so this
        // can't be a true inline overlay - see qpublicLink.ts).
        if (property.tmkNormalized) {
            rows.push(`
        <div class="sm-detail-row">
          <span class="sm-detail-label">TMK</span>
          <a href="#" class="sm-detail-value sm-tmk-link" data-tmk="${this.escapeHtml(property.tmkNormalized)}" title="View tax records on qPublic">${this.escapeHtml(property.tmkNormalized)}</a>
        </div>
      `);
        }
        if (rows.length === 0) {
            return '';
        }
        return this.renderSection('propertyDetails', 'Property Details', `
      <div class="sm-property-details">
        ${rows.join('')}
      </div>
    `);
    }
    detailRow(label, value) {
        return `
      <div class="sm-detail-row">
        <span class="sm-detail-label">${label}</span>
        <span class="sm-detail-value">${this.escapeHtml(value)}</span>
      </div>
    `;
    }
    attachEventListeners() {
        const sections = this.options.container.querySelectorAll('.sm-property-section');
        sections.forEach((section) => {
            const header = section.querySelector('.sm-section-header');
            const sectionId = section.getAttribute('data-section');
            header?.addEventListener('click', () => {
                const isCollapsed = section.classList.toggle('collapsed');
                if (sectionId) {
                    this.sectionState[sectionId] = !isCollapsed;
                }
            });
        });
        const tmkLink = this.options.container.querySelector('.sm-tmk-link');
        tmkLink?.addEventListener('click', (e) => {
            e.preventDefault();
            const tmk = tmkLink.dataset.tmk;
            if (tmk)
                openQpublicPopup(tmk);
        });
    }
    updateListing(listing) {
        this.options.listing = listing;
        this.render();
    }
    updateProperty(property) {
        this.options.property = property;
        this.render();
    }
    formatPrice(amount) {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(amount);
    }
    formatDate(dateStr) {
        try {
            const date = new Date(dateStr);
            return date.toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
            });
        }
        catch {
            return dateStr;
        }
    }
    escapeHtml(str) {
        const div = document.createElement('div');
        div.textContent = str;
        return div.innerHTML;
    }
    /**
     * Sanitize HTML content - allow basic formatting tags, remove scripts/styles
     */
    sanitizeHtml(html) {
        // Create a temporary element to parse the HTML
        const temp = document.createElement('div');
        temp.innerHTML = html;
        // Remove script and style elements
        temp.querySelectorAll('script, style, iframe, object, embed').forEach(el => el.remove());
        // Remove event handlers from all elements
        temp.querySelectorAll('*').forEach(el => {
            Array.from(el.attributes).forEach(attr => {
                if (attr.name.startsWith('on')) {
                    el.removeAttribute(attr.name);
                }
            });
        });
        return temp.innerHTML;
    }
    destroy() {
        this.options.container.innerHTML = '';
    }
}
//# sourceMappingURL=PropertySections.js.map